import React, { useEffect, useRef, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BIG_FIVE_TEST_ASSESSMENT_ID,
  enneagramChartConfiguration,
  enneagramDisplayTraits,
  enneagramWings,
  filterEnneagramTraitsAndGenerateKey,
  generateContentKeyForAssessment,
  MODEL_TYPE_ENNEAGRAM,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import { Link } from "react-router-dom";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import SendToFriendButton from "../../components/send-to-friend-button";
import ShareButton from "../../components/share-button";
import {
  getCookie,
  getEnneagramBannerImage,
  getEnneagramCharacterImage,
  getEnneagramChartImage,
  getHeroSectionEnneagramChartImage,
} from "../../utils/utility-functions";

import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import ExpandableSection from "../../components/expandable-collapsible/component";

import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import { useTranslation } from "react-i18next";

Chart.register(...registerables);

const EnhancedLikabilityChart = ({ userTraits }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // Map userTraits to get labels and percentages
  const labels = userTraits?.map(
    (item) =>
      item.trait.title.charAt(0).toUpperCase() + item.trait.title.slice(1)
  ); // Capitalize first letter

  const formattedLabel = labels.map((item) => {
    return enneagramDisplayTraits(item);
  });

  const data = userTraits.map((item) => item.percentage);
  // Map colors based on the labels
  const colors = labels.map((label) => {
    const key = label.toLowerCase(); // Convert to lowercase to match the keys in the configuration
    return enneagramChartConfiguration.colors[key] || "rgba(0, 0, 0, 0.5)"; // Fallback color if key not found
  });

  useEffect(() => {
    const config = {
      type: "polarArea",
      data: {
        labels: formattedLabel,
        datasets: [
          {
            data: data,
            backgroundColor: colors,
            borderWidth: 1,
            hoverOffset: 7,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
            position: "average",
            mode: "point",
            intersect: true,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "#fff",
            titleFont: {
              family: "Arial",
              size: 12,
              weight: "bold",
              lineHeight: 1.2,
            },
            callbacks: {
              label: function (tooltipItem) {
                return `${enneagramDisplayTraits(tooltipItem.label)}: ${
                  tooltipItem.raw
                }%`;
              },
            },
          },
          datalabels: {
            display: (context) => {
              const value = context.dataset.data[context.dataIndex];
              return value > 900; // Adjust this condition as needed
            },
            formatter: (value, context) => {
              const label = context.chart.data.labels[context.dataIndex];
              if (value <= 60) {
                return `${label}`;
              } else if (value <= 80) {
                return `Wing:\n${label}`;
              } else {
                return `Primary type:\n${label}`;
              }
            },
            color: "#555",
            font: (context) => {
              const value = context.dataset.data[context.dataIndex];
              if (value <= 60) {
                return { size: 8, weight: "bold" };
              } else if (value <= 80) {
                return { size: 10, weight: "bold" };
              } else {
                return { size: 14, weight: "bold", textAlign: "center" };
              }
            },
            align: "center",
            anchor: "center",
          },
        },
        scales: {
          r: {
            ticks: {
              display: false,
              stepSize: 20,
              showLabelBackdrop: false,
              color: "#555",
              callback: (value) => `${value}%`,
            },
            grid: {
              circular: true,
              color: "rgba(0, 0, 0, 0.05)",
            },
            angleLines: {
              display: true,
              color: "rgba(0, 0, 0, 0.05)",
            },
            pointLabels: {
              display: false,
            },
          },
        },
        layout: {
          padding: 10,
        },
      },
      plugins: [ChartDataLabels],
    };

    const ctx = chartRef.current.getContext("2d");
    chartInstanceRef.current = new Chart(ctx, config);

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [labels, data, colors]); // Add colors as a dependency

  return <canvas ref={chartRef} width="500px"></canvas>;
};

const formatContentKey = (contentKey) => {
  return contentKey
    .replace(/^the/i, "The") // Capitalize "the" at the beginning
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space between lowercase and uppercase transitions
    .replace(/([A-Z])/g, " $1") // Ensure uppercase letters are spaced properly
    .trim()
    .replace(/\s{2,}/g, " "); // Remove extra spaces
};

const colorVariants = {
  purple: "from-purple-600 via-purple-500",
  teal: "from-teal-600 via-teal-500",
  blue: "from-blue-600 via-blue-500",
  red: "from-red-600 via-red-500",
  green: "from-green-600 via-green-500",
  yellow: "from-yellow-600 via-yellow-500",
  indigo: "from-indigo-600 via-indigo-500",
};

const EnneagramReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();
  // const [content, setContent] = useState(null);
  const { t } = useTranslation();
  const [contentKey, setContentKey] = useState("");
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, gender, isAuth } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }
        const contentKey = generateContentKeyForAssessment(
          traitsResponse?.userTraits,
          traitsResponse?.assessmentId
        );
        setContentKey(contentKey);

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_ENNEAGRAM[currentLocale] ||
            MODEL_TYPE_ENNEAGRAM[LOCALE.EN],
          contentKey: contentKey,
        });
        if (contentResponse) {
          const { content } = contentResponse;

          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  const content = contentData;
  const wings = filterEnneagramTraitsAndGenerateKey(
    contentKey,
    userTraits,
    enneagramWings
  );

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  const personalityGroup = currentAssessmentAttemptData?.personality?.role;
  const personalityName = currentAssessmentAttemptData?.nameEnglishCode;

  const getCharacterImage = getEnneagramCharacterImage(
    personalityGroup,
    personalityName,
    false,
    gender || userData?.gender
  );

  const TipitoSection = () => {
    return (
      <img
        className="h-full w-auto max-h-[400px] object-contain"
        src={getCharacterImage}
        alt={"enneagram"}
      />
    );
  };

  const HeroBanner = ({ title, color = "purple" }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple; // Default fallback
    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-indigo-600`}
      >
        {/* Background Image (Hidden on small screens) */}
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-auto md:h-full object-cover object-right"
        />

        {/* Gradient Overlay */}
        <div
          className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
        />
        <div className="absolute inset-0 bg-black opacity-10" />

        {/* Hero Text */}
        <div className="relative h-full flex items-center ">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            <h2 className="text-4xl md:text-4xl font-bold mb-2 shadow-text text-white">
              {title}
            </h2>
          </div>
        </div>
      </div>
    );
  };
  const renderContentSection = (heading, content, color = "purple") => (
    <div className={`bg-${color}-50 p-6 rounded-lg shadow-md mb-6`}>
      <h3 className="text-2xl font-bold mb-4">{heading}</h3>
      <p className="text-gray-700">{content}</p>
    </div>
  );

  const renderListSection = (data, color = "purple") => {
    if (!data || !data.Content) return null;

    return (
      <div className="space-y-6 mb-8">
        <h3 className="text-2xl font-bold">{data.RootHeading}</h3>
        {data.FlavorText && (
          <p className="text-gray-600 mb-6">{data.FlavorText}</p>
        )}

        <div className={`bg-${color}-50 p-6 rounded-lg shadow-md`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {data.Content.map((item, index) => (
              <div key={index}>
                <h4 className="text-xl font-semibold mb-2">{item.Heading}</h4>
                <p className="text-gray-700">{item.Content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };
  const renderInteractingSection = (data) => {
    if (!data || !data.Content) return null; // Return null if no data

    return (
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">
          {data.RootHeading}
        </h3>
        {data.Content.map((category, index) => (
          <div className="mb-6" key={index}>
            <h4 className="text-xl font-semibold mb-4">{category.Heading}</h4>
            <p className="text-gray-600 mb-4">{category.FlavorText}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.Content.map((item, itemIndex) => (
                <div
                  className="bg-purple-50 p-6 rounded-lg shadow-md"
                  key={itemIndex}
                >
                  <h5 className="font-semibold mb-2">{item.Heading}</h5>
                  <p className="text-gray-700">{item.Content}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const CallToActionSection = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {data.CTA1 || t("report.enneagram.explore_more")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.discover_more_about_your_personality_type")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("dashboard.take_test_cta")}
            </button>
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {data.CTA2 || t("report.enneagram.learn_more")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t(
                "report.enneagram.explore_different_aspects_of_your_personality"
              )}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${BIG_FIVE_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("dashboard.take_test_cta")}
            </button>
          </div>
        </div>
      </section>
    );
  };

  const bannerImage = getEnneagramBannerImage();

  const renderGraphSection = () => {
    return (
      <img
        className="h-full w-auto max-h-[400px] object-contain"
        src={getHeroSectionEnneagramChartImage(contentKey, currentLocale)}
        alt="image"
      />
    );
  };

  const WingsSection = ({ data, rawType, displayType }) => {
    if (!data) return null;
    const primaryWingKey = Object.keys(data.PrimaryWing)[0];
    const primaryWing = data.PrimaryWing[primaryWingKey];
    const secondaryWingKey = Object.keys(data.SecondaryWing)[0];
    const secondaryWing = data.SecondaryWing[secondaryWingKey];
    const { Growth: growth, Stress: stress } = data;

    return (
      <div className="mb-8">
        <h3 className="text-2xl font-bold text-gray-800 mb-4">
          {t("report.enneagram.wings_growth_pattern")}
        </h3>
        <p className="text-gray-600 mb-6">{data.Content || ""}</p>
        <div className="space-y-8">
          {/* Primary and Secondary Wings */}
          <div>
            <h4 className="text-xl font-semibold mb-4">
              {t("report.enneagram.primary_and_secondary_wings")}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-indigo-50 p-6 rounded-lg shadow-md">
                <h5 className="text-lg font-semibold mb-2">
                  {primaryWing?.Heading || ""}
                </h5>
                <p className="text-gray-700">{primaryWing?.Content || ""}</p>
              </div>
              <div className="bg-white-50 p-6 rounded-lg flex items-center justify-center">
                <img
                  src={getEnneagramChartImage(contentKey, "w", currentLocale)}
                  // src={enneagramImagesW[contentKey] || wTheAdmirer} // Default fallback
                  alt={`${formatContentKey(contentKey)} image`}
                />
              </div>
              <div className="bg-orange-50 p-6 rounded-lg shadow-md">
                <h5 className="text-lg font-semibold mb-2">
                  {secondaryWing?.Heading || ""}
                </h5>
                <p className="text-gray-700">{secondaryWing?.Content || ""}</p>
              </div>
            </div>
          </div>

          {/* Growth and Stress Patterns */}
          <div>
            <h4 className="text-xl font-semibold mb-4">
              {t("report.enneagram.growth_and_stress_patterns")}
            </h4>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-green-50 p-6 rounded-lg shadow-md">
                <h5 className="text-lg font-semibold mb-2">
                  {growth?.Heading || ""}
                </h5>
                <p className="text-gray-700">{growth?.Content || ""}</p>
              </div>
              <div className="bg-white-50 p-6 rounded-lg flex items-center justify-center">
                <img
                  src={getEnneagramChartImage(contentKey, "gs", currentLocale)}
                  // src={enneagramImageGs[contentKey] || gsTheAdmirer} // Default fallback
                  alt={`${formatContentKey(contentKey)} image`}
                />
              </div>
              <div className="bg-fuchsia-100 p-6 rounded-lg shadow-md">
                <h5 className="text-lg font-semibold mb-2">
                  {stress?.Heading || ""}
                </h5>
                <p className="text-gray-700">{stress?.Content || ""}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!content) return null;

    return (
      <>
        <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <ExpandableSection
            defaultExpanded={true}
            heroBanner={<HeroBanner title={contentData.type} color="purple" />}
          >
            {isAuth && (
              <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                <ShareButton
                  currentAssessmentAttemptData={currentAssessmentAttemptData}
                  userGUID={userGUID}
                  userData={userData}
                />
                <SendToFriendButton
                  currentAssessmentAttemptData={currentAssessmentAttemptData}
                  userGUID={userGUID}
                  userData={userData}
                  CreateGuestUserConnect={sendToFriendReportOnEmailConnect}
                />
              </div>
            )}

            <div className="p-8 sm:p-12">
              <div className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                  <div className="md:col-span-2">
                    <h3 className="text-2xl font-bold mb-4">
                      {contentData.header}
                    </h3>
                    <p className="md:text-xl text-gray-600">
                      {" "}
                      {contentData.overview}
                    </p>
                  </div>
                  <div className="flex-1 flex items-center justify-center">
                    <TipitoSection />
                  </div>
                  <div className="flex-1 flex items-center justify-center md:col-span-2">
                    {/* <EnhancedLikabilityChart userTraits={userTraits} /> */}
                    {renderGraphSection()}
                  </div>
                </div>
                <div className="mb-8 mt-6">
                  <WingsSection data={content.additionalInsights} />
                </div>
              </div>
            </div>
          </ExpandableSection>
        </section>

        <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
          <ExpandableSection
            heroBanner={
              <HeroBanner
                title={contentData?.coreFears?.RootHeading}
                color="teal"
              />
            }
          >
            <div className="p-8 sm:p-12">
              <div className="mb-8">
                {renderListSection(content.coreMotivations, "blue")}
                {renderListSection(content.coreFears, "red")}
                {renderListSection(content.coreDesires, "green")}
                {renderListSection(content.keyTraits, "yellow")}
                {renderListSection(content.strengths, "indigo")}
                {renderListSection(content.challenges, "purple")}
                {renderContentSection(
                  t("report.enneagram.growth_tips"),
                  content.growthTips,
                  "green"
                )}
                {renderInteractingSection(content.interacting)}
              </div>
            </div>
          </ExpandableSection>
        </section>
        <CallToActionSection data={content} />
      </>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              {/* <EnneagramChart results={[{ type: 1, growth: 7, stress: 4 }]} /> */}
              {renderContent()}
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default EnneagramReport;
